import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  InternalSharedReportUpdateStatus,
  FamilyAccountUpdateStatus,
  notificationsRead,
  NotificationsShown
} from "apiServices/reducer/apiService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function NotificationActionApproveReject({
  show,
  onHide,
  rowData = {},
  onActionComplete,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.apiService.login);
  const token = userData?.Token;
  const isProvider = userData?.user_type_name === "Provider";
  const [loading, setLoading] = useState(false);
  const [backendMessage, setBackendMessage] = useState("");
  const [showBackendModal, setShowBackendModal] = useState(false);

  const handleUpdateStatus = async (approved_yn) => {
    setLoading(true);
    const formData = {
      ApprovedYN: approved_yn,
      IsApproved: approved_yn,
      LoginUserID: userData.user_id,
      token
    };

    try {
      let result;
      if (rowData.share_id) {
        formData.ShareID = rowData.share_id;
        result = await dispatch(InternalSharedReportUpdateStatus(formData)).unwrap();
      } else if (rowData.family_account_id) {
        formData.FamilyAccountID = rowData.family_account_id;
        result = await dispatch(FamilyAccountUpdateStatus(formData)).unwrap();
      }
      await dispatch(notificationsRead({ userId: userData.user_id, notificationId: rowData.notification_id, token }));
      if (result) {
        setBackendMessage(result.message || (approved_yn ? "Approved" : "Rejected"));
        setShowBackendModal(true);
        if (onActionComplete) {
          onActionComplete(rowData.notification_id);
        }
        onHide(true);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleView = async () => {
    try {
      // Mark notification as shown to remove it from notifications list
      await dispatch(NotificationsShown({
        user_id: userData.user_id,
        notification_id: rowData.notification_id,
        token: userData.Token
      })).unwrap();

      // Also mark as read to ensure it's completely removed
      await dispatch(notificationsRead({
        user_id: userData.user_id,
        notification_id: rowData.notification_id,
        token: userData.Token
      })).unwrap();
      
      // Call onActionComplete to refresh notifications list
      if (onActionComplete) {
        onActionComplete(rowData.notification_id);
      }
      
      // Set localStorage items like handlePatientSummary does
      localStorage.setItem('patientUserId', rowData.shared_from_id);
      localStorage.setItem('patientUserName', rowData.shared_from_name);
      
      onHide(true);
      
      // Navigate with state to trigger patient summary view like TbListDetails click
      navigate("/landing-page", { 
        state: { 
          shared_by: rowData.shared_from_name, 
          shared_from_id: rowData.shared_from_id,
          showPatientSummary: true, // Flag to indicate we want to show patient summary
          isFromDetails: true // Flag to match the TbListDetails click behavior
        } 
      });
    } catch (error) {
      console.error("Error removing notification:", error);
      // Even if there's an error, still navigate to view reports
      localStorage.setItem('patientUserId', rowData.shared_from_id);
      localStorage.setItem('patientUserName', rowData.shared_from_name);
      onHide(true);
      navigate("/landing-page", { 
        state: { 
          shared_by: rowData.shared_from_name, 
          shared_from_id: rowData.shared_from_id,
          showPatientSummary: true,
          isFromDetails: true
        } 
      });
    }
  };

  const handleCancel = async () => {
    // Mark notification as read and delete it when cancel is clicked
    await dispatch(notificationsRead({ userId: userData.user_id, notificationId: rowData.notification_id, token }));
    
    // Call onActionComplete to refresh notifications list
    if (onActionComplete) {
      onActionComplete(rowData.notification_id);
    }
    
    onHide(true);
  };

  return (
    <Modal show={show} onHide={() => onHide(false)} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="modal-header-bg">
        <Modal.Title className="text-capitalize fw-bold">Action</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {rowData?.comments && (
          <p className="mb-3 text-start" style={{ fontWeight: 400 }}>
            {rowData.comments}
          </p>
        )}

        {rowData?.shared_to_name && !isProvider && (
          <div className="text-center">
            <p className="fw-bold d-block " style={{ fontSize: "12px" }}>Requested By</p>
            <p className="text-info fw-bold text-capitalize m-0 " style={{ fontSize: "20px" }}>{rowData.shared_to_name}</p>
          </div>
        )}

        {!isProvider && rowData?.notes && (
          <>
          <div className="text-center " style={{ marginTop: "10px" }}>
            <small className="text-muted">Notes</small>
            <p className=" fw-bold text-capitalize m-0 " style={{ whiteSpace: "break-word", fontSize: "12px", marginTop: "5px" }}>{rowData.notes}</p>
            </div>
          </>
        )}
      </Modal.Body>

      {rowData?.approved_yn === null && (
        <Modal.Footer className="justify-content-center">
          <Button
            variant="success"
            onClick={() => handleUpdateStatus(true)}
            disabled={loading}
            className="px-4 text-capitalize p-2"
            style={{ backgroundColor: "var(--pmr-success)", borderColor: "var(--pmr-success)" }}
          >
            Approve
          </Button>
          <Button
            variant="danger"
            onClick={() => handleUpdateStatus(false)}
            disabled={loading}
            className="px-4 mx-2 text-capitalize p-2"
            style={{ borderColor: "var(--pmr-danger)", backgroundColor: "var(--pmr-danger)" }}
          >
            Reject
          </Button>
          <Button
            variant="info"
            onClick={handleCancel}
            className="px-4 text-capitalize p-2"
            style={{ borderColor: "var(--pmr-secondary)", backgroundColor: "var(--pmr-secondary)", color: "var(--pmr-white)" }}
          >
            Later
          </Button>
        </Modal.Footer>
      )}

      {rowData?.approved_yn === true && rowData?.share_id != null && (
        <Modal.Footer className="justify-content-center">
          <Button variant="success" onClick={handleView} className="text-capitalize">
            View Shared Reports
          </Button>
        </Modal.Footer>
      )}
    </Modal>

  );
}
